/**
 * ═══════════════════════════════════════════════════════════════════
 *  Tour State Machine — Manages tour lifecycle & step progression
 * ═══════════════════════════════════════════════════════════════════
 *
 *  States:  IDLE → RUNNING → COMPLETED | SKIPPED
 *                    ↕
 *                  PAUSED
 */

// ── Actions ─────────────────────────────────────────────────────────
export const TOUR_ACTIONS = {
  START: 'START',
  NEXT: 'NEXT',
  PREV: 'PREV',
  SKIP: 'SKIP',
  PAUSE: 'PAUSE',
  RESUME: 'RESUME',
  COMPLETE: 'COMPLETE',
  GO_TO: 'GO_TO',
  RESET: 'RESET',
};

// ── States ──────────────────────────────────────────────────────────
export const TOUR_STATUS = {
  IDLE: 'IDLE',
  RUNNING: 'RUNNING',
  PAUSED: 'PAUSED',
  COMPLETED: 'COMPLETED',
  SKIPPED: 'SKIPPED',
};

// ── Initial State ───────────────────────────────────────────────────
export function createInitialState(steps = []) {
  return {
    status: TOUR_STATUS.IDLE,
    steps,
    stepIndex: 0,
    totalSteps: steps.length,
    history: [],           // visited step indices
  };
}

// ── Reducer ─────────────────────────────────────────────────────────
export function tourReducer(state, action) {
  switch (action.type) {
    case TOUR_ACTIONS.START: {
      const steps = action.steps || state.steps;
      return {
        ...state,
        status: TOUR_STATUS.RUNNING,
        steps,
        stepIndex: 0,
        totalSteps: steps.length,
        history: [0],
      };
    }

    case TOUR_ACTIONS.NEXT: {
      const next = state.stepIndex + 1;
      if (next >= state.totalSteps) {
        return { ...state, status: TOUR_STATUS.COMPLETED };
      }
      return {
        ...state,
        stepIndex: next,
        history: [...state.history, next],
      };
    }

    case TOUR_ACTIONS.PREV: {
      const prev = Math.max(0, state.stepIndex - 1);
      return {
        ...state,
        stepIndex: prev,
        history: [...state.history, prev],
      };
    }

    case TOUR_ACTIONS.GO_TO: {
      const idx = Math.max(0, Math.min(action.stepIndex, state.totalSteps - 1));
      return {
        ...state,
        stepIndex: idx,
        history: [...state.history, idx],
      };
    }

    case TOUR_ACTIONS.SKIP:
      return { ...state, status: TOUR_STATUS.SKIPPED };

    case TOUR_ACTIONS.PAUSE:
      return { ...state, status: TOUR_STATUS.PAUSED };

    case TOUR_ACTIONS.RESUME:
      return { ...state, status: TOUR_STATUS.RUNNING };

    case TOUR_ACTIONS.COMPLETE:
      return { ...state, status: TOUR_STATUS.COMPLETED };

    case TOUR_ACTIONS.RESET:
      return createInitialState(state.steps);

    default:
      return state;
  }
}
